import type { FunctionComponent } from 'react';
import { Fragment, useCallback } from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';

import { Button, ButtonSize, Rounding } from '../../../shared/components/button/button';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { slugify } from '../../../shared/utils/strings';
import { IActivationStatus, ICreatorName } from '../../queries/budget-limit.graphql';
import type { ISessionLimitsContinuousResponse } from '../../queries/session-limit-continuous.graphql';
import { LimitStatusHistory } from '../limit-status-history/limit-status-history';

import styles from './session-limit-continuous-history-table.module.scss';

const style = createStyleHelper(styles, 'session-limit-continuous-history-table');

export interface ISessionLimitHistoryTableProps {
    history: ISessionLimitsContinuousResponse[];
    onLimitCancel: (id: string) => void;
}

function CancelLimit({
    onLimitCancel,
    id,
    disabled,
    isPendingCancellation,
}: {
    id: string;
    onLimitCancel: (id: string) => void;
    disabled: boolean;
    isPendingCancellation: boolean;
}) {
    const onClick = useCallback(() => onLimitCancel(id), [id, onLimitCancel]);

    return (
        <Button
            className={style('cancel')}
            size={ButtonSize.MEDIUM}
            rounding={Rounding.SMALL}
            warning
            onClick={onClick}
            disabled={disabled}
        >
            <FormattedMessage
                id={
                    isPendingCancellation
                        ? 'limit-history-table.revoke-limit.label'
                        : 'limit-history-table.cancel-limit.label'
                }
                defaultMessage={isPendingCancellation ? 'Revoke' : 'Cancel Limit'}
            />
        </Button>
    );
}

export const SessionLimitContinuousHistoryTable: FunctionComponent<
    ISessionLimitHistoryTableProps
> = ({ history, onLimitCancel }) => {
    const isDesktop = useDesktop();

    if (!history || history.length === 0) {
        return (
            <div className={style('no-data')}>
                <FormattedMessage
                    id="session-limit-history.no-data-to-display"
                    defaultMessage="No data to display"
                />
            </div>
        );
    }

    return (
        <div className={style()}>
            <table className={style('table')}>
                <thead>
                    <tr className={style('headers')}>
                        <th className={style('header', ['added'])}>
                            <FormattedMessage
                                id="session-limit-history.heading.added"
                                defaultMessage="Added"
                            />
                        </th>
                        <th className={style('header', ['creator'])}>
                            <FormattedMessage
                                id="session-limit-history.heading.creator"
                                defaultMessage="Creator"
                            />
                        </th>
                        <th className={style('header', ['limit'])}>
                            <FormattedMessage
                                id="session-limit-history.heading.time-limit"
                                defaultMessage="Time Limit"
                            />
                        </th>
                        <th className={style('header', ['cooldown'])}>
                            <FormattedMessage
                                id="session-limit-history.heading.cooldown"
                                defaultMessage="Cooldown"
                            />
                        </th>
                        {isDesktop ? (
                            <th className={style('header', ['expires'])}>
                                <FormattedMessage
                                    id="session-limit-history.heading.expires"
                                    defaultMessage="Expires"
                                />
                            </th>
                        ) : null}
                        <th className={style('header', ['status'])}>
                            <FormattedMessage
                                id="session-limit-history.heading.status"
                                defaultMessage="Status"
                            />
                        </th>
                        {isDesktop ? (
                            <th className={style('header', ['action'])}>
                                <FormattedMessage
                                    id="session-limit-history.heading.action"
                                    defaultMessage="Action"
                                />
                            </th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {history.map(
                        ({
                            activationStatus,
                            createdOn,
                            duration,
                            id,
                            isCancellable,
                            isPendingCancellation,
                            creator,
                            triggerCooldown,
                            pendingChangeExpectedOn,
                        }) => (
                            <Fragment key={id}>
                                <tr key={`${id}-info`} className={style('row')}>
                                    <td className={style('item', ['added'])}>
                                        <FormattedDate value={createdOn ? createdOn : ''} />
                                        <br />
                                        <FormattedTime
                                            value={createdOn ? createdOn : ''}
                                            timeStyle="short"
                                        />
                                    </td>
                                    <td className={style('item', ['creator'])}>
                                        <FormattedMessage
                                            id={`session-limit-history.creator.${slugify(
                                                creator ? creator : '',
                                            )}`}
                                            defaultMessage={creator ? creator : ''}
                                        />
                                    </td>
                                    <td className={style('item', ['limit'])}>
                                        <FormattedMessage
                                            id={`limit-history.period.${slugify(
                                                duration ? duration : '',
                                            )}`}
                                            defaultMessage={duration ? duration : ''}
                                        />
                                    </td>
                                    <td className={style('item', ['cooldown'])}>
                                        <FormattedMessage
                                            id={`limit-history.period.${slugify(
                                                triggerCooldown ? triggerCooldown : '',
                                            )}`}
                                            defaultMessage={triggerCooldown ? triggerCooldown : ''}
                                        />
                                    </td>
                                    {isDesktop ? (
                                        <td className={style('item', ['expires'])}>
                                            {pendingChangeExpectedOn ? (
                                                <>
                                                    <FormattedDate
                                                        value={
                                                            pendingChangeExpectedOn
                                                                ? pendingChangeExpectedOn
                                                                : ''
                                                        }
                                                    />
                                                    <br />
                                                    <FormattedTime
                                                        value={
                                                            pendingChangeExpectedOn
                                                                ? pendingChangeExpectedOn
                                                                : ''
                                                        }
                                                        timeStyle="short"
                                                    />
                                                </>
                                            ) : (
                                                <FormattedMessage
                                                    id="session-limit-history.expired.not-applicable"
                                                    defaultMessage="N/A"
                                                />
                                            )}
                                        </td>
                                    ) : null}
                                    <td
                                        className={style('item', [
                                            `status-${slugify(
                                                activationStatus ? activationStatus : '',
                                            )}`,
                                        ])}
                                    >
                                        <LimitStatusHistory
                                            activationStatus={
                                                activationStatus ?? IActivationStatus.Active
                                            }
                                            isPendingCancellation={isPendingCancellation ?? false}
                                        />
                                    </td>
                                    {isDesktop ? (
                                        <td className={style('item', ['action'])}>
                                            {isCancellable ? (
                                                <CancelLimit
                                                    id={id ? id : ''}
                                                    onLimitCancel={onLimitCancel}
                                                    isPendingCancellation={!!isPendingCancellation}
                                                    disabled={creator !== ICreatorName.User}
                                                />
                                            ) : null}
                                        </td>
                                    ) : null}
                                </tr>

                                {!isDesktop && (pendingChangeExpectedOn || isCancellable) ? (
                                    <tr
                                        key={`${id}-cancel`}
                                        className={style('row', { 'divide-x': !isDesktop })}
                                    >
                                        <td className={style('item', ['action'])} colSpan={5}>
                                            {pendingChangeExpectedOn ? (
                                                <span>
                                                    <FormattedMessage
                                                        id="session-limit-history.pending-change-expected-on.expires-on"
                                                        defaultMessage="Expires on: {expires, date, short} {expires, time, short}"
                                                        values={{
                                                            expires: new Date(
                                                                pendingChangeExpectedOn,
                                                            ),
                                                        }}
                                                    />
                                                </span>
                                            ) : null}

                                            {isCancellable ? (
                                                <CancelLimit
                                                    id={id ? id : ''}
                                                    onLimitCancel={onLimitCancel}
                                                    isPendingCancellation={!!isPendingCancellation}
                                                    disabled={creator !== ICreatorName.User}
                                                />
                                            ) : null}
                                        </td>
                                    </tr>
                                ) : null}
                            </Fragment>
                        ),
                    )}
                </tbody>
            </table>
        </div>
    );
};
